/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AppVersionTableCellProps } from 'src/luis/appShell/managePane/appVersionsPage/components/AppVersionTableCellProps';

const messages = defineMessages({
    stagingSlotName: {
        id: 'apps.appVersionPublishedDateTableCell.stagingSlotName',
        defaultMessage: 'Staging'
    },
    productionSlotName: {
        id: 'apps.appVersionPublishedDateTableCell.productionSlotName',
        defaultMessage: 'Production'
    },
    notPublishedYetNotice: {
        id: 'apps.appVersionPublishedDateTableCell.notPublishedYetNotice',
        defaultMessage: 'Not published yet'
    }
});

/**
 * Represents the cell that renders the application version
 * published date along with the slot that this version was
 * published on.
 *
 * @param props The input to this cell.
 */
export const AppVersionPublishedDateTableCell = (props: AppVersionTableCellProps) => {
    const { version, versionStates } = props;
    const { localize } = useLocalization();

    /**
     * Gets the version published dates strings based on the
     * published version states. Examples:
     *
     * - Staging: 1/1/1975
     * - Production: 1/1/1975
     *
     * ... etc.
     *
     * @param versionId The version id to get the published dates for.
     * @returns A string describing the published dates for each slot
     * for the given version id.
     */
    const getVersionDateStringParts = (): string[] => {
        const stringParts: string[] = [];

        /**
         * If the state object is not defined or both the slot parameters inside the state object
         * are not defined, then view the version's published or return a not published message.
         */
        if (!versionStates || (!versionStates.productionSlotMetaData && !versionStates.stagingSlotMetaData)) {
            return [version.publishedDate ? version.publishedDate.toLocaleDateString() : localize(messages.notPublishedYetNotice)];
        }

        if (versionStates.stagingSlotMetaData && versionStates.stagingSlotMetaData.id === version.id) {
            stringParts.push(`${localize(messages.stagingSlotName)}: ${versionStates.stagingSlotMetaData.date.toLocaleDateString()}`);
        }

        if (versionStates.productionSlotMetaData && versionStates.productionSlotMetaData.id === version.id) {
            stringParts.push(`${localize(messages.productionSlotName)}: ${versionStates.productionSlotMetaData.date.toLocaleDateString()}`);
        }

        if (!stringParts.length) {
            return [version.publishedDate ? version.publishedDate.toLocaleDateString() : localize(messages.notPublishedYetNotice)];
        }

        return stringParts;
    };

    const parts = getVersionDateStringParts();

    return (
        <Stack verticalFill verticalAlign="center" tokens={{ childrenGap: 5 }}>
            {parts.map((part, index) => (
                <LuisText variant="small" key={`datePart-${index}`}>
                    {part}
                </LuisText>
            ))}
        </Stack>
    );
};
